import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { TimeSession, TimerState, UserTimerSettings } from '@/types';
import type { RootState } from '../index';

// Paused task state structure
interface PausedTaskState {
  taskId: string;
  sessionId: string;
  elapsed: number;
  taskTitle?: string;
}

interface TimerSliceState extends TimerState {
  sessions: TimeSession[];
  currentSession: TimeSession | null;
  settings: UserTimerSettings | null;
  isLoading: boolean;
  isSessionLoading: boolean; // True while fetching active session on app load
  error: string | null;
  // Track paused tasks separately
  pausedTasks: Record<string, PausedTaskState>;
  // Track accumulated time per task
  taskTimeSpent: Record<string, number>;
}

const initialState: TimerSliceState = {
  isRunning: false,
  isPaused: false,
  taskId: null,
  startTime: null,
  elapsed: 0,
  allocated: 0,
  pauseStartTime: null,
  sessions: [],
  currentSession: null,
  settings: null,
  isLoading: false,
  isSessionLoading: true, // Start as true - we don't know if there's an active session yet
  error: null,
  pausedTasks: {},
  taskTimeSpent: {},
};

// Fetch user timer settings
export const fetchTimerSettings = createAsyncThunk(
  'timer/fetchTimerSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/timer/settings');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch timer settings'
      );
    }
  }
);

// Update user timer settings
export const updateTimerSettings = createAsyncThunk(
  'timer/updateTimerSettings',
  async (settings: Partial<UserTimerSettings>, { rejectWithValue }) => {
    try {
      const response = await api.put('/timer/settings', settings);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update timer settings'
      );
    }
  }
);

// Helper to get the most recent open session
const getMostRecentOpenSession = (sessions: TimeSession[]): TimeSession | undefined => {
  const openSessions = sessions.filter((s: TimeSession) => !s.endTime);
  if (openSessions.length === 0) return undefined;
  // Sort by startTime descending and get the most recent
  return openSessions.sort((a, b) =>
    new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
  )[0];
};

// Keys for localStorage persistence (only for optimization, not source of truth)
const ACTIVE_TASK_KEY = 'daydrive_active_task_id';

// Helper to save active task to localStorage for faster restore
const saveActiveTaskId = (taskId: string | null) => {
  if (taskId) {
    localStorage.setItem(ACTIVE_TASK_KEY, taskId);
  } else {
    localStorage.removeItem(ACTIVE_TASK_KEY);
  }
};

// Fetch all active and paused sessions from database (single API call)
export const fetchActiveSession = createAsyncThunk(
  'timer/fetchActiveSession',
  async (_, { rejectWithValue }) => {
    try {
      // Single API call to get all active/paused sessions
      const response = await api.get('/tasks/sessions/active');
      const { activeSession: serverActiveSession, pausedSessions: serverPausedSessions } = response.data;

      let activeSession: { session: TimeSession; taskId: string; taskTitle: string; estimatedMinutes?: number; elapsed: number } | null = null;
      const pausedSessions: Array<{ taskId: string; sessionId: string; elapsed: number }> = [];

      // Process active session
      if (serverActiveSession) {
        const elapsed = calculateElapsed(serverActiveSession.session);
        activeSession = {
          session: serverActiveSession.session,
          taskId: serverActiveSession.taskId,
          taskTitle: serverActiveSession.taskTitle,
          estimatedMinutes: serverActiveSession.estimatedMinutes,
          elapsed,
        };
        saveActiveTaskId(serverActiveSession.taskId);
      }

      // Process paused sessions
      if (serverPausedSessions && serverPausedSessions.length > 0) {
        for (const pausedSession of serverPausedSessions) {
          const elapsed = calculateElapsed(pausedSession.session);
          pausedSessions.push({
            taskId: pausedSession.taskId,
            sessionId: pausedSession.session.id,
            elapsed,
          });
        }
      }

      return { activeSession, pausedSessions };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch active session'
      );
    }
  }
);

// Start a new session (with auto-pause of current)
export const startNewSession = createAsyncThunk(
  'timer/startNewSession',
  async (
    { taskId, allocated = 0 }: { taskId: string; allocated?: number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const { timer } = state;

      // Auto-pause current running task if different
      if (timer.taskId && timer.taskId !== taskId && timer.isRunning && !timer.isPaused && timer.currentSession) {
        await api.post(`/tasks/${timer.taskId}/sessions/${timer.currentSession.id}/pause`);
      }

      // Check if target task has any open sessions
      const sessionsRes = await api.get(`/tasks/${taskId}/sessions`);
      const sessions = sessionsRes.data;
      const openSessions = sessions.filter((s: TimeSession) => !s.endTime);
      const mostRecentSession = getMostRecentOpenSession(sessions);

      if (mostRecentSession) {
        if (mostRecentSession.sessionStatus === 'paused') {
          // Resume the paused session
          const response = await api.post(`/tasks/${taskId}/sessions/${mostRecentSession.id}/resume`);
          return { session: response.data, taskId, allocated, resumed: true };
        } else if (mostRecentSession.sessionStatus === 'active') {
          // Check if this is OUR currently active session
          if (timer.taskId === taskId && timer.currentSession?.id === mostRecentSession.id) {
            return { session: mostRecentSession, taskId, allocated, resumed: false, alreadyActive: true };
          }
          // It's an active session we don't know about - could be stale, close it
          try {
            await api.post(`/tasks/${taskId}/sessions/${mostRecentSession.id}/stop`);
          } catch {
            // Ignore
          }
        }

        // Close any other open sessions (stale)
        for (const session of openSessions) {
          if (session.id !== mostRecentSession.id) {
            try {
              await api.post(`/tasks/${taskId}/sessions/${session.id}/stop`);
            } catch {
              // Ignore
            }
          }
        }
      }

      // Start new session
      const response = await api.post(`/tasks/${taskId}/sessions`);
      return { session: response.data, taskId, allocated, resumed: false };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to start session'
      );
    }
  }
);

// Start a session (simple version)
export const startSession = createAsyncThunk(
  'timer/startSession',
  async ({ taskId, allocated }: { taskId: string; allocated: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/sessions`);
      return { session: response.data, taskId, allocated };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to start session'
      );
    }
  }
);

// Pause session
export const pauseSession = createAsyncThunk(
  'timer/pauseSession',
  async ({ taskId, sessionId }: { taskId: string; sessionId: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const elapsed = state.timer.elapsed;

      const response = await api.post(`/tasks/${taskId}/sessions/${sessionId}/pause`);
      return { session: response.data, taskId, elapsed };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to pause session'
      );
    }
  }
);

// Resume session
export const resumeSession = createAsyncThunk(
  'timer/resumeSession',
  async ({ taskId, sessionId }: { taskId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/sessions/${sessionId}/resume`);
      return { session: response.data, taskId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to resume session'
      );
    }
  }
);

// Stop session (save time without completing task)
export const stopSession = createAsyncThunk(
  'timer/stopSession',
  async (
    { taskId, sessionId, notes }: { taskId: string; sessionId: string; notes?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/tasks/${taskId}/sessions/${sessionId}/stop`, { notes });
      return { session: response.data, taskId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to stop session'
      );
    }
  }
);

// Complete session (stop timer + mark task as completed)
export const completeSession = createAsyncThunk(
  'timer/completeSession',
  async ({ taskId, sessionId }: { taskId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/sessions/${sessionId}/complete`);
      return { session: response.data, taskId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to complete session'
      );
    }
  }
);

// Fetch task sessions
export const fetchTaskSessions = createAsyncThunk(
  'timer/fetchTaskSessions',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}/sessions`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch sessions'
      );
    }
  }
);

// Calculate elapsed time from session
const calculateElapsed = (session: TimeSession): number => {
  if (!session?.startTime) return 0;
  const start = new Date(session.startTime).getTime();
  const pauseDuration = (session.totalPauseDuration || 0) * 1000;

  if (session.sessionStatus === 'paused' && session.pauseTime) {
    const pausePoint = new Date(session.pauseTime).getTime();
    return Math.max(0, Math.floor((pausePoint - start - pauseDuration) / 1000));
  }

  return Math.max(0, Math.floor((Date.now() - start - pauseDuration) / 1000));
};

const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    tick: (state) => {
      if (state.isRunning && !state.isPaused) {
        // Simply increment by 1 second - more reliable than recalculating
        state.elapsed += 1;
      }
    },
    setElapsed: (state, action: PayloadAction<number>) => {
      state.elapsed = action.payload;
    },
    extendTime: (state, action: PayloadAction<number>) => {
      state.allocated += action.payload;
    },
    resetTimer: (state) => {
      state.isRunning = false;
      state.isPaused = false;
      state.taskId = null;
      state.startTime = null;
      state.elapsed = 0;
      state.allocated = 0;
      state.pauseStartTime = null;
      state.currentSession = null;
    },
    restoreTimerState: (state, action: PayloadAction<Partial<TimerSliceState>>) => {
      Object.assign(state, action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    // Update time spent for a specific task
    updateTaskTimeSpent: (state, action: PayloadAction<{ taskId: string; timeSpent: number }>) => {
      state.taskTimeSpent[action.payload.taskId] = action.payload.timeSpent;
    },
    // Clear paused task entry
    clearPausedTask: (state, action: PayloadAction<string>) => {
      delete state.pausedTasks[action.payload];
    },
    // Restore session state from tasks (tasks already include activeSession)
    restoreFromTasks: (state, action: PayloadAction<Array<{ id: string; title: string; timeBound?: number; activeSession?: any }>>) => {
      state.isSessionLoading = false;
      const tasks = action.payload;

      // Don't reset pausedTasks - merge instead to preserve state across dates

      for (const task of tasks) {
        if (task.activeSession) {
          const session = task.activeSession;
          const start = new Date(session.startTime).getTime();
          const pauseDuration = (session.totalPauseDuration || 0) * 1000;

          // Calculate elapsed time
          let elapsed: number;
          if (session.sessionStatus === 'paused' && session.pauseTime) {
            const pausePoint = new Date(session.pauseTime).getTime();
            elapsed = Math.max(0, Math.floor((pausePoint - start - pauseDuration) / 1000));
          } else {
            elapsed = Math.max(0, Math.floor((Date.now() - start - pauseDuration) / 1000));
          }

          if (session.sessionStatus === 'active') {
            // Set as active running session
            state.currentSession = session;
            state.taskId = task.id;
            state.elapsed = elapsed;
            state.isRunning = true;
            state.isPaused = false;
            state.startTime = start;
            if (task.timeBound) {
              state.allocated = task.timeBound;
            }
          } else if (session.sessionStatus === 'paused') {
            // Add to paused tasks
            state.pausedTasks[task.id] = {
              taskId: task.id,
              sessionId: session.id,
              elapsed,
              taskTitle: task.title,
            };
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch timer settings
      .addCase(fetchTimerSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      // Update timer settings
      .addCase(updateTimerSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      // Fetch active session
      .addCase(fetchActiveSession.pending, (state) => {
        state.isSessionLoading = true;
      })
      .addCase(fetchActiveSession.fulfilled, (state, action) => {
        state.isSessionLoading = false;
        if (action.payload) {
          const { activeSession, pausedSessions } = action.payload;

          // Restore all paused sessions to pausedTasks
          for (const paused of pausedSessions) {
            state.pausedTasks[paused.taskId] = {
              taskId: paused.taskId,
              sessionId: paused.sessionId,
              elapsed: paused.elapsed,
            };
          }

          // Restore active session if exists
          if (activeSession) {
            const { session, taskId, estimatedMinutes, elapsed } = activeSession;
            state.currentSession = session;
            state.taskId = taskId;
            state.elapsed = elapsed;
            state.isRunning = true;
            state.isPaused = false;
            state.startTime = new Date(session.startTime).getTime();

            if (estimatedMinutes) {
              state.allocated = estimatedMinutes * 60;
            }
          }
        }
      })
      .addCase(fetchActiveSession.rejected, (state) => {
        state.isSessionLoading = false;
      })
      // Start new session (with auto-pause)
      .addCase(startNewSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startNewSession.fulfilled, (state, action) => {
        const { session, taskId, allocated, resumed, alreadyActive } = action.payload;

        // Save active task ID for quick restore on reload
        saveActiveTaskId(taskId);

        // Save previous running task to pausedTasks if it was auto-paused
        if (state.taskId && state.taskId !== taskId && state.isRunning && !state.isPaused && state.currentSession) {
          state.pausedTasks[state.taskId] = {
            taskId: state.taskId,
            sessionId: state.currentSession.id,
            elapsed: state.elapsed,
          };
        }

        // Get the paused elapsed time before removing from pausedTasks
        const pausedElapsed = state.pausedTasks[taskId]?.elapsed;

        // Remove from pausedTasks if resuming
        if (resumed && state.pausedTasks[taskId]) {
          delete state.pausedTasks[taskId];
        }

        state.isLoading = false;
        state.isRunning = true;
        state.isPaused = false;
        state.taskId = taskId;
        state.currentSession = session;
        state.allocated = allocated;
        state.pauseStartTime = null;

        if (resumed) {
          // When resuming, prefer stored elapsed time, fallback to calculated
          const elapsedToUse = pausedElapsed !== undefined ? pausedElapsed : calculateElapsed(session);
          state.elapsed = elapsedToUse;
          // Set startTime so that tick() continues correctly from here
          state.startTime = Date.now() - (elapsedToUse * 1000);
        } else if (alreadyActive) {
          state.elapsed = calculateElapsed(session);
          state.startTime = new Date(session.startTime).getTime();
        } else {
          state.elapsed = 0;
          state.startTime = Date.now();
        }
      })
      .addCase(startNewSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Start session (simple)
      .addCase(startSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRunning = true;
        state.isPaused = false;
        state.taskId = action.payload.taskId;
        state.startTime = Date.now();
        state.elapsed = 0;
        state.allocated = action.payload.allocated;
        state.pauseStartTime = null;
        state.currentSession = action.payload.session;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Pause session
      .addCase(pauseSession.fulfilled, (state, action) => {
        const { session, taskId } = action.payload;
        state.isPaused = true;
        state.pauseStartTime = Date.now();
        state.currentSession = session;
        state.elapsed = calculateElapsed(session);

        // Track in pausedTasks
        state.pausedTasks[taskId] = {
          taskId,
          sessionId: session.id,
          elapsed: state.elapsed,
        };
      })
      // Resume session
      .addCase(resumeSession.fulfilled, (state, action) => {
        const { session, taskId } = action.payload;
        state.isPaused = false;
        state.startTime = Date.now() - state.elapsed * 1000;
        state.pauseStartTime = null;
        state.currentSession = session;
        state.elapsed = calculateElapsed(session);

        // Remove from pausedTasks
        delete state.pausedTasks[taskId];
      })
      // Stop session
      .addCase(stopSession.fulfilled, (state, action) => {
        const { session, taskId } = action.payload;

        // Clear active task from localStorage
        saveActiveTaskId(null);

        // Update accumulated time for this task
        if (session.duration) {
          state.taskTimeSpent[taskId] = (state.taskTimeSpent[taskId] || 0) + session.duration;
        }

        state.sessions.push(session);
        state.isRunning = false;
        state.isPaused = false;
        state.taskId = null;
        state.startTime = null;
        state.elapsed = 0;
        state.allocated = 0;
        state.pauseStartTime = null;
        state.currentSession = null;

        // Remove from pausedTasks
        delete state.pausedTasks[taskId];
      })
      // Complete session
      .addCase(completeSession.fulfilled, (state, action) => {
        const { session, taskId } = action.payload;

        // Clear active task from localStorage
        saveActiveTaskId(null);

        if (session.duration) {
          state.taskTimeSpent[taskId] = (state.taskTimeSpent[taskId] || 0) + session.duration;
        }

        state.sessions.push(session);
        state.isRunning = false;
        state.isPaused = false;
        state.taskId = null;
        state.startTime = null;
        state.elapsed = 0;
        state.allocated = 0;
        state.pauseStartTime = null;
        state.currentSession = null;

        delete state.pausedTasks[taskId];
      })
      // Fetch sessions
      .addCase(fetchTaskSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
      });
  },
});

export const {
  tick,
  setElapsed,
  extendTime,
  resetTimer,
  restoreTimerState,
  clearError,
  updateTaskTimeSpent,
  clearPausedTask,
  restoreFromTasks,
} = timerSlice.actions;

export default timerSlice.reducer;
